import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ChevronUp, ChevronDown, X } from "lucide-react";

const sectionLabels = {
  holding: "Prayers We Are Holding",
  still_being_held: "Still Being Held",
  answer_acknowledged: "An Answer Acknowledged",
};

export default function AdminMondayVigil() {
  const [vigil, setVigil] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [search, setSearch] = useState("");
  const [addSection, setAddSection] = useState("holding");
  const [preview, setPreview] = useState(false);
  const [mobilePreview, setMobilePreview] = useState(false);
  const [testEmail, setTestEmail] = useState("");

  const loadVigil = useCallback(() => {
    api.get("/admin/vigil/draft").then(({ data }) => setVigil(data));
  }, []);
  const loadCandidates = useCallback(() => {
    const params = { moderation_status: "approved" };
    if (search) params.q = search;
    api.get("/admin/prayers", { params }).then(({ data }) => setCandidates(data.prayers));
  }, [search]);

  useEffect(() => { loadVigil(); loadCandidates(); api.get("/quotes/public").then(({ data }) => setQuotes(data.quotes)); }, [loadVigil, loadCandidates]);

  const saveField = async (patch) => {
    const { data } = await api.patch(`/admin/vigils/${vigil.id}`, patch);
    setVigil(data);
  };

  const addPrayer = async (prayerId) => {
    try {
      await api.post(`/admin/vigils/${vigil.id}/prayers`, { prayer_id: prayerId, section: addSection });
      toast("Added to Monday list.");
      loadVigil();
    } catch (e) { toast(e.response?.data?.detail || "Could not add."); }
  };
  const removePrayer = async (vpId) => { await api.delete(`/admin/vigil-prayers/${vpId}`); loadVigil(); };
  const editExcerpt = async (vpId, text) => { await api.patch(`/admin/vigil-prayers/${vpId}`, { edited_excerpt: text }); };

  const move = async (section, idx, dir) => {
    const items = [...vigil.sections[section]];
    const j = idx + dir;
    if (j < 0 || j >= items.length) return;
    [items[idx], items[j]] = [items[j], items[idx]];
    const allIds = Object.keys(vigil.sections).flatMap((s) => (s === section ? items : vigil.sections[s]).map((p) => p.vigil_prayer_id));
    await api.post(`/admin/vigils/${vigil.id}/reorder`, { ordered_ids: allIds });
    loadVigil();
  };

  const sendTest = async () => {
    if (!testEmail) { toast("Enter an email."); return; }
    const { data } = await api.post(`/admin/vigils/${vigil.id}/test-email`, { email: testEmail });
    toast(data.message);
  };
  const send = async (schedule) => {
    const { data } = await api.post(`/admin/vigils/${vigil.id}/send`, { schedule });
    toast(data.message);
    loadVigil();
  };
  const duplicate = async () => { const { data } = await api.post(`/admin/vigils/${vigil.id}/duplicate`); toast(data.message); };

  if (!vigil) return <div className="p-12 mono-label">Attending…</div>;

  const inputCls = "w-full bg-transparent border border-ink/25 px-3 py-2 font-body text-ink outline-none";

  return (
    <div className="p-6 md:p-10">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <p className="mono-label mb-3">Monday Vigil Composer</p>
          <h1 className="font-heading text-4xl md:text-5xl text-ink leading-none">Compose Monday's Vigil</h1>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setPreview(!preview)} className="btn-ink" data-testid="toggle-preview">{preview ? "Hide preview" : "Preview"}</button>
          <button onClick={duplicate} className="btn-ink" data-testid="duplicate-vigil">Duplicate</button>
        </div>
      </div>
      <p className="font-body text-ink-tertiary mt-3 italic">Delivery is MOCKED in this version — composing, preview, test and scheduling all work; no real email is sent yet.</p>

      <div className="grid lg:grid-cols-2 gap-10 mt-10">
        {/* Left: composer */}
        <div className="space-y-8">
          <div className="space-y-4">
            <label className="block"><span className="mono-label block mb-2">Title</span>
              <input value={vigil.title || ""} onChange={(e) => setVigil({ ...vigil, title: e.target.value })} onBlur={(e) => saveField({ title: e.target.value })} className={inputCls} data-testid="vigil-title" /></label>
            <label className="block"><span className="mono-label block mb-2">Opening reflection</span>
              <textarea rows={3} value={vigil.opening_reflection || ""} onChange={(e) => setVigil({ ...vigil, opening_reflection: e.target.value })} onBlur={(e) => saveField({ opening_reflection: e.target.value })} className={inputCls} data-testid="vigil-opening" /></label>
          </div>

          {/* Selected prayers by section */}
          {Object.keys(sectionLabels).map((section) => (
            <div key={section}>
              <span className="mono-label block mb-3">{sectionLabels[section]} ({vigil.sections[section].length})</span>
              <div className="space-y-2">
                {vigil.sections[section].map((p, idx) => (
                  <div key={p.vigil_prayer_id} className="border border-ink/15 p-3 bg-paper" data-testid={`vigil-selected-${p.id}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1">
                        <div className="font-heading text-lg text-ink">{p.display_name}</div>
                        <textarea rows={2} defaultValue={p.excerpt_display} onBlur={(e) => editExcerpt(p.vigil_prayer_id, e.target.value)} className="w-full bg-transparent border border-ink/15 px-2 py-1 mt-1 font-body text-sm text-ink-secondary outline-none" data-testid={`vigil-excerpt-${p.id}`} placeholder="Shortened presentation text…" />
                      </div>
                      <div className="flex flex-col gap-1">
                        <button onClick={() => move(section, idx, -1)} className="text-ink-tertiary hover:text-ink"><ChevronUp size={16} /></button>
                        <button onClick={() => move(section, idx, 1)} className="text-ink-tertiary hover:text-ink"><ChevronDown size={16} /></button>
                        <button onClick={() => removePrayer(p.vigil_prayer_id)} className="text-terracotta" data-testid={`vigil-remove-${p.id}`}><X size={16} /></button>
                      </div>
                    </div>
                  </div>
                ))}
                {vigil.sections[section].length === 0 && <p className="font-body text-sm text-ink-tertiary italic">None yet.</p>}
              </div>
            </div>
          ))}

          <label className="block"><span className="mono-label block mb-2">Words for the Way (verified quote)</span>
            <select value={vigil.quote_id || ""} onChange={(e) => saveField({ quote_id: e.target.value })} className={inputCls} data-testid="vigil-quote">
              <option value="">— none —</option>
              {quotes.map((q) => <option key={q.id} value={q.id}>{q.author} — {q.quotation.slice(0, 40)}…</option>)}
            </select></label>

          <label className="block"><span className="mono-label block mb-2">Gratitude prompt</span>
            <textarea rows={2} value={vigil.gratitude_prompt || ""} onChange={(e) => setVigil({ ...vigil, gratitude_prompt: e.target.value })} onBlur={(e) => saveField({ gratitude_prompt: e.target.value })} className={inputCls} data-testid="vigil-gratitude" /></label>
          <label className="block"><span className="mono-label block mb-2">Closing reflection</span>
            <textarea rows={2} value={vigil.closing_reflection || ""} onChange={(e) => setVigil({ ...vigil, closing_reflection: e.target.value })} onBlur={(e) => saveField({ closing_reflection: e.target.value })} className={inputCls} data-testid="vigil-closing" /></label>

          <div className="paper-rule pt-6 space-y-3">
            <div className="flex gap-2">
              <input value={testEmail} onChange={(e) => setTestEmail(e.target.value)} placeholder="test@email.com" className={inputCls} data-testid="test-email-input" />
              <button onClick={sendTest} className="btn-ink whitespace-nowrap" data-testid="send-test">Send test</button>
            </div>
            <div className="flex gap-2">
              <button onClick={() => send(true)} className="btn-ink" data-testid="schedule-vigil">Schedule Monday</button>
              <button onClick={() => send(false)} className="btn-solid" data-testid="send-vigil">Send now</button>
            </div>
            <span className="mono-label">Status: {vigil.status}</span>
          </div>
        </div>

        {/* Right: candidate picker OR preview */}
        <div>
          {preview ? (
            <div className="border border-ink/25 sticky top-4">
              <div className="flex justify-between items-center p-3 border-b border-ink/15 bg-paper-tertiary">
                <span className="mono-label">Email preview</span>
                <button onClick={() => setMobilePreview(!mobilePreview)} className="mono-label underline" data-testid="preview-device">{mobilePreview ? "Desktop" : "Mobile"}</button>
              </div>
              <div className={`p-8 bg-paper overflow-y-auto ${mobilePreview ? "max-w-sm mx-auto" : ""}`} style={{ maxHeight: "70vh" }} data-testid="email-preview">
                <p className="mono-label text-center">Ministry of Answered Prayers</p>
                <h2 className="font-heading text-3xl text-center text-ink mt-2">{vigil.title || "Monday Vigil"}</h2>
                {vigil.opening_reflection && <p className="font-body text-ink-secondary mt-6 leading-relaxed italic">{vigil.opening_reflection}</p>}
                {Object.keys(sectionLabels).map((s) => vigil.sections[s].length > 0 && (
                  <div key={s} className="mt-8">
                    <h3 className="font-heading text-xl text-ink border-b border-ink/15 pb-1">{sectionLabels[s]}</h3>
                    {vigil.sections[s].map((p) => (
                      <div key={p.vigil_prayer_id} className="mt-4">
                        <div className="font-heading text-lg text-ink">{p.display_name}</div>
                        <p className="font-body text-ink-secondary leading-relaxed">{p.excerpt_display}</p>
                      </div>
                    ))}
                  </div>
                ))}
                {vigil.quote && (
                  <div className="mt-8">
                    <h3 className="font-heading text-xl text-ink border-b border-ink/15 pb-1">Words for the Way</h3>
                    <blockquote className="font-heading italic text-xl text-ink mt-3">“{vigil.quote.quotation}”<footer className="font-body text-sm text-ink-secondary mt-2">— {vigil.quote.author}</footer></blockquote>
                  </div>
                )}
                <div className="mt-8">
                  <h3 className="font-heading text-xl text-ink border-b border-ink/15 pb-1">A Prayer of Gratitude</h3>
                  <p className="font-body text-ink mt-3 italic">{vigil.gratitude_prompt}</p>
                </div>
                {vigil.closing_reflection && <p className="font-body text-ink-secondary mt-6 italic">{vigil.closing_reflection}</p>}
                <p className="font-heading italic text-xl text-terracotta text-center mt-8">May my life today become an answer to someone's prayer.</p>
              </div>
            </div>
          ) : (
            <div className="sticky top-4">
              <span className="mono-label block mb-3">Add prayers · placing into:</span>
              <select value={addSection} onChange={(e) => setAddSection(e.target.value)} className={inputCls + " mb-3"} data-testid="add-section-select">
                {Object.keys(sectionLabels).map((s) => <option key={s} value={s}>{sectionLabels[s]}</option>)}
              </select>
              <div className="flex gap-2 mb-3">
                <input value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => e.key === "Enter" && loadCandidates()} placeholder="Search approved prayers" className={inputCls} data-testid="candidate-search" />
                <button onClick={loadCandidates} className="btn-ink whitespace-nowrap">Search</button>
              </div>
              <div className="border border-ink/15 overflow-y-auto" style={{ maxHeight: "60vh" }}>
                {candidates.map((p) => (
                  <div key={p.id} className="p-3 border-b border-ink/10 flex items-start justify-between gap-3 bg-paper" data-testid={`candidate-${p.id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="font-heading text-lg text-ink">{p.display_name || p.title}</div>
                      <div className="mono-label">{p.category} · {p.consent_monday ? "Monday OK" : "no Monday consent"} · held {p.holders_count}</div>
                    </div>
                    <button onClick={() => addPrayer(p.id)} className="mono-label border border-ink/25 px-2 py-1 hover:bg-ink hover:text-paper whitespace-nowrap" data-testid={`add-candidate-${p.id}`}>+ Add</button>
                  </div>
                ))}
                {candidates.length === 0 && <div className="p-8 text-center font-body text-ink-tertiary italic">No approved prayers found.</div>}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
